import axios, {AxiosResponse} from 'axios'
import {ID, Response} from '../../../../../_metronic/helpers'
import {Badge, Fee, BadgesQueryResponse, FeesQueryResponse, FeeType, Document} from './_models'
import moment from 'moment'

const API_URL = process.env.REACT_APP_API_URL
const BRAND_URL = `${API_URL}/collaborator/badge`
const GET_BRANDS_URL = `${API_URL}/collaborator/badge`
const FEE_URL = `${API_URL}/collaborator/fees`
const AVAILABLITY_URL = `${API_URL}/collaborator/availablity`
const CONTRACT_URL = `${API_URL}/collaborator/contract`
const GET_FEE_URL = `${API_URL}/collaborator/fees`
const DOCUMENT_URL = `${API_URL}/collaborator/document`
const CONTRACT_USER_URL = `${API_URL}/wages/contract`

const getDocumentsArray = (collaborator: any): Promise<Array<Document>> => {
  return axios.get(`${DOCUMENT_URL}/${collaborator._id}`).then((d: AxiosResponse<any>) => {
    return d.data.data
  })
}

const downloadContract = (id: any): Promise<Blob> => {
  return axios
    .get(`${CONTRACT_USER_URL}/pdf/${id}`, {responseType: 'blob'})
    .then((response: AxiosResponse<Blob>) => response.data)
}

const getContractsArray = (collaborator: any, period: string): Promise<Array<any>> => {
  const API_URL_BASE = process.env.REACT_APP_API_URL
  return axios
    .get(`${API_URL_BASE}/contract`, {
      params: {
        lookup: '',
        filter_period: period,
        collaborator: collaborator._id,
      },
    })
    .then((d: AxiosResponse<any>) => {
      return d.data.data
    })
}

const downloadDocument = (id: string, path: string): void => {
  axios({
    url: `${DOCUMENT_URL}/${id}`,
    method: 'GET',
    responseType: 'blob',
  })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', path)
      document.body.appendChild(link)
      link.click()
    })
    .catch((error) => {
      console.error("Erreur lors du téléchargement de l'image :", error)
    })
}

const uploadDocument = (collaborator: any, formData: any): Promise<void> => {
  return axios.post(`${DOCUMENT_URL}`, formData).then(() => {})
}

const deleteDocument = (document: Document): Promise<void> => {
  return axios.delete(`${DOCUMENT_URL}/${document._id}`).then(() => {})
}

const getBadges = (query: string): Promise<BadgesQueryResponse> => {
  return axios
    .get(`${GET_BRANDS_URL}?${query}`)
    .then((d: AxiosResponse<BadgesQueryResponse>) => d.data)
}

const getBadgesArray = (): Promise<Array<Location>> => {
  return axios.get(`${BRAND_URL}`).then((d: AxiosResponse<any>) => {
    return d.data.data
  })
}

const getBadgeById = (id: ID): Promise<Badge | undefined> => {
  return axios
    .get(`${BRAND_URL}/${id}`)
    .then((response: AxiosResponse<Response<Badge>>) => response.data)
    .then((response: Response<Badge>) => response.data)
}

const createBadge = (badge: Badge): Promise<Badge | undefined> => {
  return axios
    .post(BRAND_URL, badge)
    .then((response: AxiosResponse<Response<Badge>>) => response.data)
    .then((response: Response<Badge>) => response.data)
}

const updateBadge = (badge: Badge): Promise<Badge | undefined> => {
  return axios
    .put(`${BRAND_URL}/${badge._id}`, badge)
    .then((response: AxiosResponse<Response<Badge>>) => response.data)
    .then((response: Response<Badge>) => response.data)
}

const deleteBadge = (badgeId: ID): Promise<void> => {
  return axios.delete(`${BRAND_URL}/${badgeId}`).then(() => {})
}

const deleteSelectedBadges = (badgeIds: Array<ID>): Promise<void> => {
  const requests = badgeIds.map((id) => axios.delete(`${BRAND_URL}/${id}`))
  return axios.all(requests).then(() => {})
}

const getFees = (query: string): Promise<FeesQueryResponse> => {
  return axios.get(`${GET_FEE_URL}?${query}`).then((d: AxiosResponse<FeesQueryResponse>) => d.data)
}

const getFeesArray = (): Promise<Array<FeeType>> => {
  return axios.get(`${FEE_URL}`).then((d: AxiosResponse<any>) => {
    return d.data.data
  })
}

const getFeeById = (id: ID): Promise<Fee | undefined> => {
  return axios
    .get(`${FEE_URL}/${id}`)
    .then((response: AxiosResponse<Response<Fee>>) => response.data)
    .then((response: Response<Fee>) => response.data)
}

const createFee = (fee: Fee, collaborator: any): Promise<Fee | undefined> => {
  return axios
    .post(`${FEE_URL}/${collaborator._id}`, fee)
    .then((response: AxiosResponse<Response<Fee>>) => response.data)
    .then((response: Response<Fee>) => response.data)
}

const createAvailablity = (
  availablity: Array<Object>,
  collaborator: any
): Promise<Array<Object> | undefined> => {
  return axios
    .put(`${AVAILABLITY_URL}/${collaborator._id}`, {
      availablity: availablity.map((av) => JSON.stringify(av)),
    })
    .then((d: AxiosResponse<any>) => {
      return d.data.data
    })
}

const createContractCdi = (
  type: string,
  amount: any,
  collaborator: any
): Promise<Array<Object> | undefined> => {
  // Normalize amount: convert comma to dot for backend compatibility
  const normalizedAmount = typeof amount === 'string'
    ? parseFloat(amount.replace(',', '.'))
    : amount

  return axios
    .post(`${CONTRACT_URL}/${collaborator._id}`, {
      type,
      amount: normalizedAmount,
    })
    .then((d: AxiosResponse<any>) => {
      return d.data.data
    })
}

const createContractCddu = (
  type: string,
  structure: any,
  collaborator: any
): Promise<Array<Object> | undefined> => {
  const normalizedPricing = (structure || []).map((pricing: any) => ({
    brand: pricing.brand ?? null,
    amount:
      typeof pricing.amount === 'string'
        ? parseFloat(pricing.amount.replace(',', '.'))
        : pricing.amount,
  }))

  return axios
    .post(`${CONTRACT_URL}/${collaborator._id}`, {
      type,
      pricing: normalizedPricing,
    })
    .then((d: AxiosResponse<any>) => {
      return d.data.data
    })
}

const updateFee = (fee: Fee, collaborator: any): Promise<Fee | undefined> => {
  return axios
    .put(`${FEE_URL}/${collaborator._id}`, fee)
    .then((response: AxiosResponse<Response<Fee>>) => response.data)
    .then((response: Response<Fee>) => response.data)
}

const deleteFee = (fee: Fee, collaborator: any): Promise<void> => {
  return axios
    .delete(
      `${FEE_URL}/${collaborator._id}?date=${moment(fee.date).format('YYYY-MM-DD')}&fee=${
        fee.category
      }`
    )
    .then(() => {})
}

const deleteSelectedFees = (badgeIds: Array<ID>): Promise<void> => {
  const requests = badgeIds.map((id) => axios.delete(`${FEE_URL}/${id}`))
  return axios.all(requests).then(() => {})
}

export {
  getBadges,
  deleteBadge,
  deleteSelectedBadges,
  getBadgeById,
  createBadge,
  updateBadge,
  getBadgesArray,
  getFees,
  deleteFee,
  deleteSelectedFees,
  getFeeById,
  createFee,
  updateFee,
  getFeesArray,
  createAvailablity,
  createContractCdi,
  createContractCddu,
  getDocumentsArray,
  deleteDocument,
  uploadDocument,
  downloadDocument,
  getContractsArray,
  downloadContract,
}
